/**
 * Pretty-print JSON **and** record which lines each path occupies, in one walk.
 *
 * Needed because the API Payload tab highlights individual fields (see
 * `docs/copy-apply-search-payload.md` §6.2.4). Locating them by matching the
 * rendered text with a regex would be wrong: `label`, `user`, `export`,
 * `enrichment` and `searchList` all occur under **both** `contact` and
 * `account`, so a textual match cannot tell the two apart.
 *
 * Output is byte-identical to `JSON.stringify(value, null, indent)` — the spec
 * asserts that, so the displayed payload can't drift from real JSON.
 */

export type LineRange = {
  /** 1-based, inclusive. */
  start: number;
  /** 1-based, inclusive. Spans the whole block for objects/arrays. */
  end: number;
};

export type StringifyResult = {
  json: string;
  /** Dot-path (array indices as segments) -> the lines it occupies. */
  ranges: Map<string, LineRange>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function stringifyWithLineMap(
  value: unknown,
  indent = 2
): StringifyResult {
  const lines: string[] = [];
  const ranges = new Map<string, LineRange>();
  const pad = (depth: number) => ' '.repeat(indent * depth);

  /**
   * `prefix` already carries this value's indent and (for object members) its
   * `"key": `. `trailing` is the comma, if any.
   */
  function emit(
    val: unknown,
    path: string,
    prefix: string,
    trailing: string,
    depth: number
  ): void {
    const start = lines.length + 1;

    if (Array.isArray(val)) {
      if (val.length === 0) {
        lines.push(`${prefix}[]${trailing}`);
      } else {
        lines.push(`${prefix}[`);
        val.forEach((item, i) => {
          emit(
            item,
            path ? `${path}.${i}` : String(i),
            pad(depth + 1),
            i === val.length - 1 ? '' : ',',
            depth + 1
          );
        });
        lines.push(`${pad(depth)}]${trailing}`);
      }
    } else if (isRecord(val)) {
      // `undefined` members are skipped, matching JSON.stringify.
      const entries = Object.entries(val).filter(
        ([, child]) => child !== undefined
      );

      if (entries.length === 0) {
        lines.push(`${prefix}{}${trailing}`);
      } else {
        lines.push(`${prefix}{`);
        entries.forEach(([key, child], i) => {
          emit(
            child,
            path ? `${path}.${key}` : key,
            `${pad(depth + 1)}${JSON.stringify(key)}: `,
            i === entries.length - 1 ? '' : ',',
            depth + 1
          );
        });
        lines.push(`${pad(depth)}}${trailing}`);
      }
    } else {
      lines.push(`${prefix}${JSON.stringify(val) ?? 'null'}${trailing}`);
    }

    if (path) ranges.set(path, { start, end: lines.length });
  }

  emit(value, '', '', '', 0);
  return { json: lines.join('\n'), ranges };
}

/**
 * Expand `paths` into a `line -> note` map, so the viewer can look a line up in
 * O(1). An object/array field covers its whole block, so every line of a
 * multi-line filter value carries the same note.
 */
export function buildLineNotes(
  ranges: Map<string, LineRange>,
  paths: ReadonlyArray<{ path: string; note: string }>
): Map<number, string> {
  const notes = new Map<number, string>();
  paths.forEach(({ path, note }) => {
    const range = ranges.get(path);
    if (!range) return;
    for (let line = range.start; line <= range.end; line += 1) {
      notes.set(line, note);
    }
  });
  return notes;
}
